import {
  ACTION_ARGS,
  Arg,
  COMPONENT_STATE_ARGS,
  INPUT_FORM_ARGS,
  INPUT_MENU_ARGS,
  INPUT_TRIGGER_ARGS,
  Metadata,
  pick,
} from '../../../../../utils/spds/metadata';
import { action } from '@ember/object';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { tracked } from '@glimmer/tracking';
import Button from '../../../core/button.gjs';
import FlexRow from '../../../layout/flex/row.gjs';
import MultiselectClearFilter from '../../multiselect-clear-filter.gjs';
import Super from '../../../core/base.gjs';
import shiftFocusToParent from '../../../../../modifiers/shift-focus-to-parent';
import styles from './trigger.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

const METADATA = Metadata.define({
  description: 'Trigger button and controls for the dropdown list component',
  extends: [
    pick(COMPONENT_STATE_ARGS, [
      'autofocus',
      'disabled',
      'error',
      { name: 'open', required: true },
    ]),
    pick(INPUT_MENU_ARGS, ['multiselect']),
    pick(ACTION_ARGS, ['onClick', { name: 'onClose', required: true }]),
    pick(INPUT_FORM_ARGS, ['placeholder']),
    pick(INPUT_TRIGGER_ARGS, ['menuId', { name: 'triggerId', required: true }]),
  ],
  meta: {
    clearSelection: Arg.action({
      description: 'Clears the current selection (wired by the parent dropdown for multiselect)',
      private: true,
    }),
    selected: Arg.object({ description: 'Currently selected option(s)' }),
  },
  name: 'Dropdown List Trigger',
});

export default class SpdsInputDropdownListTrigger extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-input-dropdown-list-trigger-container';
  classesToMap = ['disabled', 'error', 'open'];
  elementsToRegister = [{ name: 'trigger', selector: 'button' }];
  styles = styles;

  @tracked clearFilterComponent;

  get buttonClasses() {
    let classes = ['spds-input-dropdown-list-trigger'];

    if (this.hasMultiselectOptions || this.hasSelectedOption) classes.push('active');

    return classes.join(' ');
  }

  get hasMultiselectOptions() {
    let { multiselect, selected } = this.args;

    return multiselect && selected?.length > 0;
  }

  get hasSelectedOption() {
    let { multiselect, selected } = this.args;

    return !multiselect && selected;
  }

  get text() {
    let { placeholder, selected } = this.args;

    if (this.hasMultiselectOptions) return '';

    return selected && !Array.isArray(selected) ? selected?.textContent.trim() : placeholder;
  }

  @action
  clearSelection(value, evt) {
    evt.stopPropagation();
    evt.target.classList.remove('active');

    this.args.clearSelection(...arguments);
  }

  <template>
    <FlexRow
      class={{this.classes}}
      data-test-spds-input-dropdown-list-trigger-container
      {{this.setupComponent}}
      {{this.validateArguments}}
    >
      <Button
        @autofocus={{@autofocus}}
        aria-controls={{@menuId}}
        class={{this.buttonClasses}}
        data-test-spds-input-dropdown-list-trigger
        disabled={{@disabled}}
        id={{@triggerId}}
        {{on "click" @onClick}}
        {{shiftFocusToParent}}
        ...attributes
      >
        <FlexRow @tag="span" class="spds-input-dropdown-list-trigger-text-container">
          {{this.text}}
        </FlexRow>
        {{svgJar "dropdown-caret" class="spds-input-dropdown-list-trigger-caret"}}
      </Button>
      {{#if @multiselect}}
        <MultiselectClearFilter
          @disabled={{@disabled}}
          @clearSelection={{@clearSelection}}
          @registerComponent={{fn this.registerComponent "clearFilterComponent"}}
          @selected={{@selected}}
          @unregisterComponent={{fn this.unregisterComponent "clearFilterComponent"}}
        />
      {{/if}}
    </FlexRow>
  </template>
}

export const DROPDOWN_LIST_TRIGGER_METADATA = SpdsInputDropdownListTrigger.metadata;
